import React, { type ReactNode } from 'react';
import { styled, keyframes } from '@compiled/react';
import isNil from 'lodash/isNil';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';

type Props = {
	cardContent: MessageDescriptor;
	icon: ReactNode;
	statistic?: number | string;
	showLoadingState: boolean;
};

export const Card = ({ icon, statistic, cardContent, showLoadingState }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<Wrapper>
			{icon}
			<ContentWrapper>
				{formatMessage(cardContent, {
					statistic: isNil(statistic) ? '-' : statistic,
					description: (chunks: ReactNode[]) => <Description>{chunks}</Description>,
					header: (chunks: ReactNode[]) => <Header>{chunks}</Header>,
					stats: (chunks: ReactNode[]) => (
						<>
							{showLoadingState ? (
								<StatisticPlaceholder data-testId="project-create-importer-upload-flow.ui.select-third-party-entity-screen.entity-stats.card.statistic-placeholder" />
							) : (
								<Heading as="span" size="large">
									{chunks}
								</Heading>
							)}
						</>
					),
				})}
			</ContentWrapper>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	borderRadius: '8px',
	width: '400px',
	minHeight: '112px',
	overflow: 'hidden',
	display: 'flex',
	flexFlow: 'row',
	alignItems: 'stretch',
	boxShadow: token(
		'elevation.shadow.raised',
		'0px 1px 1px rgba(9, 30, 66, 0.25),0px 0px 1px rgba(9, 30, 66, 0.31)',
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: `${token('elevation.surface', colors.N0)}`,
	boxSizing: 'border-box',
	marginTop: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.span({
	display: 'block',
	font: token('font.body.UNSAFE_small'),
	fontWeight: token('font.weight.semibold'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N400),
	marginTop: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.span({
	font: token('font.body.small'),
	display: 'block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N400),
	whiteSpace: 'wrap',
	marginTop: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.p({
	marginTop: '0',
	padding: token('space.200', '16px'),
	boxSizing: 'border-box',
	maxWidth: '288px',
	overflowX: 'hidden',
	textOverflow: 'ellipsis',
	width: '100%',
	whiteSpace: 'nowrap',
	height: '100%',
});

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-20px 0',
	},
	'100%': {
		backgroundPosition: '200px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatisticPlaceholder = styled.span({
	display: 'block',
	height: token('space.100', '8px'),
	margin: `${token('space.150', '12px')} 0`,
	width: token('space.600', '48px'),
	borderRadius: '8px',
	animationDuration: '2s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationName: shimmer,
	animationTimingFunction: 'linear',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.skeleton', colors.N30),
	backgroundImage: `linear-gradient( 107.35deg, ${token(
		'color.skeleton',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		colors.N30,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	)} 10%, ${token('color.skeleton.subtle', colors.N20)} 30%, ${token(
		'color.skeleton',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		colors.N30,
	)} 50% )`,
});
